import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { Info, X } from 'lucide-react';
import { ShiftMarketplaceRequest } from '../../types';
import { marketplaceSchedulerBadge } from '../../utils/shiftMarketplacePending';

const APPROVALS_PATH = '/shift-marketplace/approvals';

function parseLocalDateTime(dateTimeStr: string): Date {
  if (!dateTimeStr) return new Date();
  try {
    if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
      const [datePart, timePart] = dateTimeStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    return parseISO(dateTimeStr);
  } catch {
    return new Date(dateTimeStr);
  }
}

function formatShiftRange(start?: string, end?: string): string {
  if (!start || !end) return '—';
  const s = parseLocalDateTime(start);
  const e = parseLocalDateTime(end);
  return `${format(s, 'EEE d MMM HH:mm')} – ${format(e, 'HH:mm')}`;
}

function typeLabel(request: ShiftMarketplaceRequest): string {
  return request.requestType === 'Swap' ? 'Swap' : 'Cover';
}

function statusLabel(status: string): string {
  if (status === 'PendingApproval') return 'Pending approval';
  if (status === 'Open') return 'Offered';
  return status;
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 text-sm">
      <div className="text-gray-500">{label}</div>
      <div className="text-gray-900">{value}</div>
    </div>
  );
}

export function MarketplaceCoverDetailsModal({
  request,
  onClose,
}: {
  request: ShiftMarketplaceRequest;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const title = marketplaceSchedulerBadge(request);
  const offered = request.offeredShift;
  const requested = request.requestedShift;

  const modal = (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="marketplace-cover-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 id="marketplace-cover-details-title" className="text-base font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-2">
          <DetailRow label="Type" value={typeLabel(request)} />
          <DetailRow label="Status" value={statusLabel(request.status)} />
          <DetailRow label="Location" value={request.locationName} />
          <DetailRow label="Offered by" value={request.offeredByName} />
          <DetailRow label="Accepted by" value={request.acceptedByName} />
          <DetailRow
            label="Offered shift"
            value={
              offered
                ? `${formatShiftRange(offered.startTime, offered.endTime)}${
                    offered.positionTitle ? ` · ${offered.positionTitle}` : ''
                  }`
                : undefined
            }
          />
          {requested && (
            <DetailRow
              label="Requested shift"
              value={`${formatShiftRange(requested.startTime, requested.endTime)}${
                requested.employeeName ? ` · ${requested.employeeName}` : ''
              }`}
            />
          )}
          {request.notes && <DetailRow label="Notes" value={request.notes} />}
        </div>

        <div className="px-4 pb-4 pt-1 border-t">
          <a
            href={APPROVALS_PATH}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            Marketplace Approvals
          </a>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export function MarketplaceCoverBadge({
  request,
  variant = 'onColor',
}: {
  request: ShiftMarketplaceRequest;
  variant?: 'onColor' | 'onLight';
}) {
  const [open, setOpen] = useState(false);
  const label = marketplaceSchedulerBadge(request);

  return (
    <>
      <div
        className={`mt-1 flex items-center gap-0.5 text-[10px] font-semibold leading-tight px-1 py-0.5 rounded ${
          variant === 'onColor' ? 'bg-amber-100 text-amber-900' : 'bg-amber-50 text-amber-800'
        }`}
      >
        <span className="min-w-0 truncate">{label}</span>
        <button
          type="button"
          title="View cover details"
          aria-label="View cover details"
          className="flex-shrink-0 p-0.5 rounded hover:bg-amber-200 text-amber-900"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen(true);
          }}
        >
          <Info className="w-3 h-3" />
        </button>
      </div>
      {open && (
        <MarketplaceCoverDetailsModal request={request} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
